import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

interface MembersState {
  members: any[];
  currentMember: any;
  pagination: {
    total: number;
    page: number;
    per_page: number;
    has_next: boolean;
  };
  loading: boolean;
  error: string | null;
  listRequestId?: string;
}

const initialState: MembersState = {
  members: [],
  currentMember: null,
  pagination: {
    total: 0,
    page: 1,
    per_page: 50,
    has_next: false,
  },
  loading: false,
  error: null,
};

export const fetchMembers = createAsyncThunk(
  'members/fetchMembers',
  async (params: { limit?: number; offset?: number; q?: string; state?: string }) => {
    const response = await apiService.getMembers(params);
    return response.data;
  }
);

export const fetchMemberDetail = createAsyncThunk(
  'members/fetchMemberDetail',
  async (bioguideId: string) => {
    const response = await apiService.getMemberDetail(bioguideId);
    return response.data;
  }
);

const membersSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {
    clearCurrentMember: (state) => {
      state.currentMember = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMembers.pending, (state, action) => {
        state.listRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMembers.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.members = action.payload.members || [];
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchMembers.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch members';
      })
      .addCase(fetchMemberDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentMember = null;
      })
      .addCase(fetchMemberDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMember = action.payload;
      })
      .addCase(fetchMemberDetail.rejected, (state, action) => {
        state.loading = false;
        state.currentMember = null;
        state.error = action.error.message || 'Failed to fetch member detail';
      });
  },
});

export const { clearCurrentMember, clearError } = membersSlice.actions;
export default membersSlice.reducer;
